import {
  FiCheckCircle,
  FiClock,
  FiCalendar,
  FiXCircle,
  FiUsers,
  FiLogIn,
  FiAlertTriangle,
} from "react-icons/fi";

const styles = {
  Present: {
    icon: <FiCheckCircle size={16} />,
    iconBg: "bg-gradient-to-br from-emerald-400 to-green-500",
    barColor: "bg-gradient-to-r from-emerald-400 to-green-500",
  },
  Late: {
    icon: <FiClock size={16} />,
    iconBg: "bg-gradient-to-br from-amber-400 to-yellow-500",
    barColor: "bg-gradient-to-r from-amber-400 to-yellow-500",
  },
  "On Leave": {
    icon: <FiCalendar size={16} />,
    iconBg: "bg-gradient-to-br from-blue-400 to-blue-500",
    barColor: "bg-gradient-to-r from-blue-400 to-blue-500",
  },
  Absent: {
    icon: <FiXCircle size={16} />,
    iconBg: "bg-gradient-to-br from-red-400 to-rose-500",
    barColor: "bg-gradient-to-r from-red-400 to-rose-500",
  },
};

export default function AttendanceOverview({
  overview = null,
  isLoading = false,
}) {
  const attendanceData = (overview?.rows || []).map((item) => ({
    ...item,
    ...(styles[item.label] || styles.Present),
  }));

  const totalEmployees = Number(overview?.totalEmployees || 0);
  const presentPercentage = Number(overview?.presentPercentage || 0);

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - presentPercentage / 100);

  const footerStats = [
    {
      label: "Check-ins",
      value: Number(overview?.checkIns || 0),
      icon: <FiLogIn size={18} />,
      iconBg: "bg-gradient-to-br from-blue-400 to-indigo-500",
      textColor: "text-gray-900",
    },
    {
      label: "Corrections Pending",
      value: Number(overview?.pendingCorrections || 0),
      icon: <FiAlertTriangle size={18} />,
      iconBg: "bg-gradient-to-br from-orange-400 to-amber-500",
      textColor: "text-orange-600",
    },
  ];

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-md">
      <div className="mb-8 flex items-center justify-between gap-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Today&apos;s Attendance Overview
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Workforce status for today
          </p>

          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1.5 text-sm text-gray-500">
            <FiUsers className="text-gray-400" size={14} />
            Total Employees:
            <span className="font-semibold text-gray-900">
              {totalEmployees}
            </span>
          </div>
        </div>

        <div className="relative h-24 w-24 shrink-0">
          <svg viewBox="0 0 100 100" className="h-24 w-24 -rotate-90">
            <defs>
              <linearGradient
                id="presentGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#34d399" />
                <stop offset="100%" stopColor="#16a34a" />
              </linearGradient>
            </defs>

            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="#f3f4f6"
              strokeWidth="10"
            />

            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="url(#presentGradient)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className="transition-all duration-700"
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold text-gray-900">
              {presentPercentage}%
            </span>

            <span className="text-xs text-gray-400">Present</span>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-sm text-gray-400">
          Loading attendance…
        </div>
      ) : attendanceData.length ? (
        <div className="space-y-5">
          {attendanceData.map((item) => (
            <div key={item.label}>
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-lg text-white ${item.iconBg}`}
                  >
                    {item.icon}
                  </div>

                  <span className="font-medium text-gray-700">
                    {item.label}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900">
                    {item.count}
                  </span>

                  <span className="w-10 text-right text-sm text-gray-500">
                    {item.percentage}%
                  </span>
                </div>
              </div>

              <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-2.5 rounded-full transition-all duration-700 ${item.barColor}`}
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-16 text-center text-sm text-gray-400">
          No attendance data available for today.
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-6 border-t border-gray-100 pt-6">
        {footerStats.map((stat) => (
          <div key={stat.label} className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl text-white ${stat.iconBg}`}
            >
              {stat.icon}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">
                {stat.label}
              </p>

              <p className={`text-lg font-bold ${stat.textColor}`}>
                {stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}